/**
 * EIE Session - Web Implementation
 */

import {
  EventEmitter,
  EmotionAPIClient,
  LocalEmotionClassifier,
  Gemma3LocalClassifier,
  type EIEConfig,
  type AnalysisMode,
  type EmotionalState,
  type Diagnosis,
  type Baseline,
  type BaselineDeviation,
  type SessionSummary,
  type EIEEventType,
  type EventCallback,
} from '@alphahub/eie-core';
import { WebFaceDetector } from './WebFaceDetector';
import { WebHandDetector } from './WebHandDetector';
import { WebPoseDetector } from './WebPoseDetector';
// WebAudioCapture is now managed by AudioMilestoneManager
import { WebHeartRateDetector } from './WebHeartRateDetector';
import { TrackingService } from './session/TrackingService';
import { SnapshotAccumulator } from './session/SnapshotAccumulator';
import { BaselineCalibrator } from './session/BaselineCalibrator';
import { UltraDetectionLoop } from './session/UltraDetectionLoop';
import { AudioMilestoneManager } from './session/AudioMilestoneManager';
import { computeDeviation, getMostFrequent } from './session/DeviationCalculator';
import { SessionStateStore } from './session/SessionStateStore';
import { LocalCalibrationRunner } from './session/LocalCalibrationRunner';
import { LightDetectionLoop } from './session/LightDetectionLoop';

export class EIESession {
  private config: any;
  private analysisMode: AnalysisMode;
  private eventEmitter: EventEmitter;
  // Legacy scaffolds kept for constructor injection compatibility
  private previewTrackingService?: TrackingService;
  private baselineCalibrator?: BaselineCalibrator;
  private audioManager?: AudioMilestoneManager;
  private ultraDetectionLoop?: UltraDetectionLoop;
  private facialDetector?: WebFaceDetector;
  private handDetector?: WebHandDetector;
  private poseDetector?: WebPoseDetector;
  // audioCapture removed; use `audioManager` instead
  private apiClient: EmotionAPIClient;
  private hrDetector: WebHeartRateDetector;
  // localClassifier is the LocalEmotionClassifier (rule-based mode removed in this branch)
  private localClassifier: any | undefined;
  
  private isRunning: boolean = false;
  private sessionId: string = '';
  private startTime: number = 0;
  private abortController: AbortController = new AbortController();
  private hrInterval?: number;
  private visibilityHandler?: () => void;

  private latestLandmarks?: Array<{ i: number; x: number; y: number; z: number }>;
  private latestBlendshapes?: Record<string, number>;
  private latestHandLandmarks?: Array<{ x: number; y: number; z?: number }>;
  private lastValidFaceDetectionTime: number = 0;

  public _dryRun: boolean = false;

  private readonly state = new SessionStateStore();

  private calibrationRunner?: LocalCalibrationRunner;
  private lightDetectionLoop?: LightDetectionLoop;

  private static isLikelyMobileRuntime(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent ?? '';
    const byUA = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
    const byPointer = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    return byUA || byPointer;
  }


  constructor(config: EIEConfig, deps?: {
    eventEmitter?: EventEmitter;
    apiClient?: EmotionAPIClient;
    hrDetector?: WebHeartRateDetector;
    trackingService?: TrackingService;
    snapshotAccumulator?: SnapshotAccumulator;
    baselineCalibrator?: BaselineCalibrator;
    audioManager?: AudioMilestoneManager;
  }) {
    this.config = this.normalizeConfig(config);
    this.analysisMode = config.analysisMode ?? 'ultra';
    this.eventEmitter = deps?.eventEmitter ?? new EventEmitter();
    this.apiClient = deps?.apiClient ?? new EmotionAPIClient(config.apiUrl, config.authToken);
    this.hrDetector = deps?.hrDetector ?? new WebHeartRateDetector(this.config.heartRate.calibrationDuration);

    // store optional scaffolds for future refactor
    this.baselineCalibrator = deps?.baselineCalibrator;
    this.audioManager = deps?.audioManager;

    if (this.analysisMode === 'light') {
      const classifierConfig = { ...config.localClassifier, isMobileRuntime: this.config.localClassifier?.isMobileRuntime ?? false };
      if (classifierConfig.engine === 'gemma3') {
        console.info('[EIE][CLASSIFIER] using Gemma3LocalClassifier (on-device LLM)');
        this.localClassifier = new Gemma3LocalClassifier(classifierConfig);
      } else {
        console.info('[EIE][CLASSIFIER] using LocalEmotionClassifier (coded rules)');
        this.localClassifier = new LocalEmotionClassifier(classifierConfig);
      }
    }
  }

  async startPreview(): Promise<void> {
    if (this.facialDetector) return; // already initialized

    if (this.config.features.facialEmotion) {
      const detectorConfig: any = {};
      if (this.config.videoSource) detectorConfig.videoElement = this.config.videoSource;
      else if (this.config.videoStream) detectorConfig.videoStream = this.config.videoStream;
      // Forward camera tuning options from EIEConfig.camera
      const cam = (this.config as any).camera ?? {};
      if (cam.lowLightEnhancement !== undefined) detectorConfig.lowLightEnhancement = cam.lowLightEnhancement;
      if (cam.brightnessBoost !== undefined) detectorConfig.brightnessBoost = cam.brightnessBoost;
      if (cam.contrastBoost !== undefined) detectorConfig.contrastBoost = cam.contrastBoost;
      if (cam.minFaceDetectionConfidence !== undefined) detectorConfig.minFaceDetectionConfidence = cam.minFaceDetectionConfidence;
      if (cam.minFacePresenceConfidence !== undefined) detectorConfig.minFacePresenceConfidence = cam.minFacePresenceConfidence;
      if (cam.minTrackingConfidence !== undefined) detectorConfig.minTrackingConfidence = cam.minTrackingConfidence;
      if (cam.grayscaleMode !== undefined) detectorConfig.grayscaleMode = cam.grayscaleMode;
      if (cam.enableSessionFaceLock !== undefined) detectorConfig.enableSessionFaceLock = cam.enableSessionFaceLock;
      if (cam.maxFaces !== undefined) detectorConfig.maxFaces = cam.maxFaces;
      if (cam.faceLockDistanceThreshold !== undefined) detectorConfig.faceLockDistanceThreshold = cam.faceLockDistanceThreshold;
      if (cam.faceLockGraceMs !== undefined) detectorConfig.faceLockGraceMs = cam.faceLockGraceMs;
      if (cam.isMobileRuntime) detectorConfig.isMobileRuntime = true;
      this.facialDetector = new WebFaceDetector(detectorConfig);
      await this.facialDetector.initialize();
    }

    if (this.config.features.bodyPose && this.facialDetector) {
      const videoEl = this.facialDetector.getVideoElement();
      if (videoEl) {
        this.poseDetector = new WebPoseDetector();
        await this.poseDetector.initialize(videoEl);
      }
    }

    // Initialize hand landmarker if we have a video element (used to improve hand-near-face detection)
    // Skip on mobile light mode: focused uses eye squint, not hand detection,
    // and the hand detector CPU delegate competes with the face detector GPU.
    const isMobileRuntime = !!((this.config as any).camera?.isMobileRuntime);
    const skipHandDetector = isMobileRuntime && this.analysisMode === 'light';
    if (this.facialDetector && !skipHandDetector) {
      const videoEl = this.facialDetector.getVideoElement();
      if (videoEl) {
        try {
          this.handDetector = this.handDetector ?? new WebHandDetector();
          await this.handDetector.initialize(videoEl, isMobileRuntime);
        } catch (err) {
          console.warn('Hand detector failed to initialize:', err);
        }
      }
    }

    // In light mode, LightDetectionLoop handles all detection at its own interval.
    // In ultra mode, start a preview-only TrackingService for visual overlay feedback.
    // It will be stopped when start() launches UltraDetectionLoop.
    if (this.analysisMode !== 'light') {
      this.previewTrackingService = new TrackingService(this.config, this.eventEmitter, {
        getFaceDetector: () => this.facialDetector,
        getPoseDetector: () => this.poseDetector,
        getHandDetector: () => this.handDetector,
      }, {
        setLatestLandmarks: (l?: any[]) => { this.latestLandmarks = l as any; },
        setLatestBlendshapes: (b?: Record<string, number>) => { this.latestBlendshapes = b; },
        setLatestHandLandmarks: (h?: any[]) => { this.latestHandLandmarks = h; },
        setLastValidFaceDetectionTime: (ts: number) => { this.lastValidFaceDetectionTime = ts; },
      });
      this.previewTrackingService.start();
    }
  }

  async start(): Promise<void> {
    if (this.isRunning) throw new Error('Session already running');

    try {
      this.sessionId = `eie_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.startTime = Date.now();
      this.isRunning = true;

      // Initialize detectors if not already done via startPreview()
      await this.startPreview();

      // Stop preview tracking — UltraDetectionLoop takes over
      if (this.previewTrackingService) {
        this.previewTrackingService.stop();
        this.previewTrackingService = undefined;
      }

      // Light mode: also load hand detector
      if (this.analysisMode === 'light') {
        this.lightDetectionLoop = this.lightDetectionLoop ?? new LightDetectionLoop(
          this.eventEmitter,
          this.state,
          {
            isRunning: () => this.isRunning,
            getConfig: () => this.config,
            getFacialDetector: () => this.facialDetector,
            getHandDetector: () => this.handDetector,
            getPoseDetector: () => this.poseDetector,
            getLocalClassifier: () => this.localClassifier,
            getLatestLandmarks: () => this.latestLandmarks,
            getLatestHandLandmarks: () => this.latestHandLandmarks,
            getSessionId: () => this.sessionId,
          },
          {
            setLatestBlendshapes: (bs) => { this.latestBlendshapes = bs; },
            setLatestLandmarks: (lms) => { this.latestLandmarks = lms as any; },
            startLocalCalibration: (anchor) => this.startLocalCalibration(anchor),
          },
        );
        this.lightDetectionLoop.start();
      } else {
        // Ultra mode: audio capture + backend API
        if (this.config.features.voiceAnalysis) {
          this.audioManager = this.audioManager ?? new AudioMilestoneManager(this.config, this.eventEmitter, this.apiClient, {
            isRunning: () => this.isRunning,
            getRecentStates: (windowMs: number) => {
              const now = Date.now();
              return this.state.timeline.filter(s => now - s.timestamp <= windowMs);
            },
            getRecentPoseSnapshots: (windowMs: number) => {
              const now = Date.now();
              return this.state.poseSnapshots.filter(p => now - p.timestamp <= windowMs);
            },
            getLastValidFaceDetectionTime: () => this.lastValidFaceDetectionTime,
            getLatestHeartRate: () => this.state.latestHeartRate,
            getAbortSignal: () => this.abortController?.signal,
            getSessionId: () => this.sessionId,
            getBaseline: () => this.state.baseline,
          }, {
            incrementPendingRequests: () => { this.state.pendingRequests++; },
            decrementPendingRequests: () => { this.state.pendingRequests--; },
            pushDiagnosis: (d: any) => { this.state.diagnoses.push(d); },
            emit: (e: string, p: any) => { this.eventEmitter.emit(e as any, p); },
            pushTimelineUpdate: (s: any) => { this.state.timeline.push(s); },
            getPendingMilestoneChunkId: () => this.state.pendingMilestoneChunkId,
            setPendingMilestoneChunkId: (id?: number) => { this.state.pendingMilestoneChunkId = id; },
          });
          await this.audioManager.start();
        }

        // Baseline calibrator (driven by UltraDetectionLoop, no own timer)
        this.baselineCalibrator = this.baselineCalibrator ?? new BaselineCalibrator(this.config, this.eventEmitter, {
          getLatestBlendshapes: () => this.latestBlendshapes,
        }, {
          setBaseline: (b: any) => { this.state.baseline = b; },
          pushTimelineState: (s: any) => { this.state.timeline.push(s); },
          emit: (e: string, p: any) => { this.eventEmitter.emit(e as any, p); },
        });
        this.baselineCalibrator.start();

        // Single coordinated detection loop for ultra mode
        // Replaces TrackingService (60fps) + SnapshotAccumulator (5s) + BaselineCalibrator timer (100ms)
        this.ultraDetectionLoop = new UltraDetectionLoop(
          this.config.intervals.emotion,
          this.eventEmitter,
          {
            getFaceDetector: () => this.facialDetector,
            getPoseDetector: () => this.poseDetector,
            getHandDetector: () => this.handDetector,
            getBaselineCalibrator: () => this.baselineCalibrator,
            isRunning: () => this.isRunning,
          },
          {
            setLatestLandmarks: (l?: any[]) => { this.latestLandmarks = l as any; },
            setLatestBlendshapes: (b?: Record<string, number>) => { this.latestBlendshapes = b; },
            setLatestHandLandmarks: (h?: any[]) => { this.latestHandLandmarks = h; },
            setLastValidFaceDetectionTime: (ts: number) => { this.lastValidFaceDetectionTime = ts; },
            pushTimelineState: (s: any) => { this.state.timeline.push(s); },
            pushPoseSnapshot: (p: any) => { this.state.poseSnapshots.push(p); },
          },
        );
        this.ultraDetectionLoop.start();
      }

      if (this.config.features.facialEmotion && this.config.videoSource) {
        const videoEl = this.config.videoSource as HTMLVideoElement;
        this.hrDetector.setCalibrationDuration(this.config.heartRate.calibrationDuration);
        this.hrDetector.reset();
        this.hrInterval = window.setInterval(() => {
          if (!this.isRunning || !this.latestLandmarks) return;
          if (videoEl.readyState < 2) return;
          const hrData = this.hrDetector.captureFrame(videoEl, this.latestLandmarks);
          if (hrData) {
            if (hrData.bpm > 0) {
              this.state.latestHeartRate = { bpm: hrData.bpm, confidence: hrData.confidence, signalQuality: hrData.signalQuality };
            }
            this.eventEmitter.emit('heartRateUpdate', hrData);
          }
        }, 67); // ~15 Hz to match HeartRateAlgorithm HR_SAMPLE_RATE

        // Pause HR sampling when page is hidden (mobile background / tab switch)
        // to avoid corrupted rPPG data from throttled timers
        this.visibilityHandler = () => {
          if (document.hidden) {
            if (this.hrInterval) { clearInterval(this.hrInterval); this.hrInterval = undefined; }
          } else if (this.isRunning && !this.hrInterval) {
            this.hrDetector.reset();
            this.hrDetector.setCalibrationDuration(this.config.heartRate.calibrationDuration);
            this.hrInterval = window.setInterval(() => {
              if (!this.isRunning || !this.latestLandmarks) return;
              if (videoEl.readyState < 2) return;
              const hrData = this.hrDetector.captureFrame(videoEl, this.latestLandmarks);
              if (hrData) {
                if (hrData.bpm > 0) {
                  this.state.latestHeartRate = { bpm: hrData.bpm, confidence: hrData.confidence, signalQuality: hrData.signalQuality };
                }
                this.eventEmitter.emit('heartRateUpdate', hrData);
              }
            }, 67);
          }
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
      }

      this.eventEmitter.emit('sessionStart', { sessionId: this.sessionId, timestamp: this.startTime });
    } catch (error) {
      // Cleanup anything that was partially initialized
      this.isRunning = false;
      try { this.ultraDetectionLoop?.stop(); } catch {}
      if (this.hrInterval) { clearInterval(this.hrInterval); this.hrInterval = undefined; }
      this.eventEmitter.emit('error', {
        code: 'SESSION_START_FAILED',
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Trigger a quick local calibration (uses the local classifier if present).
   * Useful for screen-gaze calibration flow initiated by the UI.
   */
  startLocalCalibration(screenAnchor?: { x: number; y: number }): void {
    this.calibrationRunner = this.calibrationRunner ?? new LocalCalibrationRunner(
      this.eventEmitter,
      this.state,
      {
        getLatestLandmarks: () => this.latestLandmarks,
        getSessionId: () => this.sessionId,
        getConfig: () => this.config,
        getFacialDetector: () => this.facialDetector,
        getLocalClassifier: () => this.localClassifier,
      },
      {
        clearLastStableEmotion: () => { this.state.lastStableEmotion = null; },
        clearPendingLocalCalibration: () => { this.state.pendingLocalCalibration = null; },
        runLightDetectionTick: () => { this.lightDetectionLoop?.tick(); },
      },
    );
    this.calibrationRunner.start(screenAnchor);
  }

  /**
   * Return whether the local classifier is currently calibrating.
   */
  getIsLocalCalibrating(): boolean {
    try {
      return !!this.localClassifier?.getIsCalibrating?.();
    } catch {
      return false;
    }
  }

  /** Force-release microphone if a crash left it stuck */
  forceReleaseMicrophone(): void {
    try { this.audioManager?.stop(); } catch {}
  }

  async stop(): Promise<SessionSummary> {
    if (!this.isRunning) return this.generateSummary();

    this.isRunning = false;
    // stop detection loops
    try { this.previewTrackingService?.stop(); } catch {}
    this.previewTrackingService = undefined;
    try { this.ultraDetectionLoop?.stop(); } catch {}
    this.ultraDetectionLoop = undefined;
    // DON'T abort pending API requests — let them finish so last chunks arrive

    try { this.lightDetectionLoop?.stop(); } catch {}
    this.lightDetectionLoop = undefined;
    try { this.baselineCalibrator?.stop(); } catch {}
    // trackingInterval removed; TrackingService handles RAF lifecycle
    if (this.hrInterval) {
      clearInterval(this.hrInterval);
      this.hrInterval = undefined;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }

    if (this.facialDetector) await this.facialDetector.dispose();
    if (this.handDetector) await this.handDetector.dispose();
    if (this.poseDetector) await this.poseDetector.dispose();
    try { this.audioManager?.stop(); } catch {}

    // Nullify so start() can reinitialize
    this.facialDetector = undefined;
    this.handDetector = undefined;
    this.poseDetector = undefined;
    this.state.reset();

    // Wait for pending API responses before finalizing
    if (this.state.pendingRequests > 0) {
      console.log(`⏳ Waiting for ${this.state.pendingRequests} pending API request(s)...`);
      await new Promise<void>(resolve => {
        const check = () => {
          if (this.state.pendingRequests <= 0) return resolve();
          setTimeout(check, 200);
        };
        check();
        // Safety timeout: don't wait forever
        setTimeout(resolve, 15000);
      });
    }

    const summary = this.generateSummary();
    this.eventEmitter.emit('sessionEnd', summary);
    return summary;
  }

  on(event: EIEEventType, callback: EventCallback): void {
    this.eventEmitter.on(event, callback);
  }

  off(event: EIEEventType, callback: EventCallback): void {
    this.eventEmitter.off(event, callback);
  }

  getCurrentState(): EmotionalState | undefined {
    return this.state.timeline[this.state.timeline.length - 1];
  }

  getTimeline(): EmotionalState[] {
    return [...this.state.timeline];
  }

  getBaseline(): Baseline | undefined {
    return this.state.baseline;
  }

  getBaselineDeviation(): BaselineDeviation | undefined {
    if (!this.state.baseline) return undefined;
    const current = this.getCurrentState();
    if (!current) return undefined;
    return computeDeviation(this.state.baseline, current.valence, current.arousal);
  }

  getDiagnoses(): Diagnosis[] {
    return [...this.state.diagnoses];
  }

  getVideoElement(): HTMLVideoElement | undefined {
    return this.facialDetector?.getVideoElement();
  }

  getLatestHandLandmarks(): Array<{ x: number; y: number; z?: number }> | undefined {
    return this.latestHandLandmarks;
  }

  async exportData(format: 'json' | 'csv'): Promise<string> {
    const summary = this.generateSummary();
    if (format === 'json') return JSON.stringify(summary, null, 2);
    
    const headers = ['timestamp', 'emotion', 'valence', 'arousal', 'confidence'];
    const rows = summary.timeline.map(s => [s.timestamp, s.emotion, s.valence, s.arousal, s.confidence]);
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  }

  private normalizeConfig(config: EIEConfig): any {
    const isMobile = EIESession.isLikelyMobileRuntime();
    const defaultEmotionInterval = config.analysisMode === 'light'
      ? (isMobile ? 80 : 800)
      : 5000;
      return {
        apiUrl: config.apiUrl,
        authToken: config.authToken,
        videoSource: config.videoSource,
        videoStream: config.videoStream,
        isMobileRuntime: isMobile,
        features: {
          facialEmotion: config.features?.facialEmotion ?? true,
          voiceAnalysis: config.features?.voiceAnalysis ?? true,
          heartRate: config.features?.heartRate ?? false,
          bodyPose: config.features?.bodyPose ?? false,
        },
        intervals: {
          emotion: config.intervals?.emotion ?? defaultEmotionInterval,
          milestone: config.intervals?.milestone ?? 30000,
          chunkBuffer: config.intervals?.chunkBuffer ?? 1,
        },
      heartRate: {
        calibrationDuration: config.heartRate?.calibrationDuration ?? 30,
        enabled: config.heartRate?.enabled ?? false,
      },
      privacy: {
        sendAudio: config.privacy?.sendAudio ?? true,
        sendBlendshapes: config.privacy?.sendBlendshapes ?? true,
        sendTranscript: config.privacy?.sendTranscript ?? true,
        localOnly: config.privacy?.localOnly ?? false,
      },
      localClassifier: {
        yawnThreshold: config.localClassifier?.yawnThreshold ?? 0.45,
        calibrationDuration: config.localClassifier?.calibrationDuration ?? 2500,
        suppressWhenUnengaged: config.localClassifier?.suppressWhenUnengaged ?? true,
        stabilityWindow: config.localClassifier?.stabilityWindow ?? 10,
        isMobileRuntime: isMobile,
        emotionDetectors: config.localClassifier?.emotionDetectors ?? {},
      },
      camera: {
        lowLightEnhancement: config.camera?.lowLightEnhancement ?? true,
        brightnessBoost: config.camera?.brightnessBoost ?? 1.35,
        contrastBoost: config.camera?.contrastBoost ?? 1.2,
        minFaceDetectionConfidence: config.camera?.minFaceDetectionConfidence ?? 0.2,
        minFacePresenceConfidence: config.camera?.minFacePresenceConfidence ?? 0.2,
        minTrackingConfidence: config.camera?.minTrackingConfidence ?? 0.2,
        grayscaleMode: config.camera?.grayscaleMode ?? true,
        calibrationDelay: config.camera?.calibrationDelay ?? 0,
        enableSessionFaceLock: config.camera?.enableSessionFaceLock ?? true,
        maxFaces: config.camera?.maxFaces ?? 1,
        faceLockDistanceThreshold: config.camera?.faceLockDistanceThreshold ?? 0.35,
        faceLockGraceMs: config.camera?.faceLockGraceMs ?? 1200,
        isMobileRuntime: isMobile,
      },
      // 'ultra' on-device model config removed (LLM scaffolding reverted)
    };
  }

  private generateSummary(): SessionSummary {
    const endTime = Date.now();
    const emotions = this.state.timeline.map(s => s.emotion);
    const dominantEmotion = emotions.length > 0 ? this.getMostFrequent(emotions) : 'neutral';

    return {
      sessionId: this.sessionId,
      startTime: this.startTime,
      endTime,
      duration: endTime - this.startTime,
      timeline: this.state.timeline,
      diagnoses: this.state.diagnoses,
      baseline: this.state.baseline,
      averageState: {
        valence: this.state.timeline.length > 0
          ? this.state.timeline.reduce((sum, s) => sum + s.valence, 0) / this.state.timeline.length
          : 0,
        arousal: this.state.timeline.length > 0
          ? this.state.timeline.reduce((sum, s) => sum + s.arousal, 0) / this.state.timeline.length
          : 0,
        dominantEmotion,
      },
    };
  }

  async generateLLMResponse(_prompt: string | any[], _onPartial?: (partial: string, done?: boolean) => void) {
    throw new Error('LLM support was reverted; initialize LLMAdapter manually if needed');
  }

  private getMostFrequent<T>(arr: T[]): T {
    return getMostFrequent(arr);
  }

}
